// Target Weights: the Portfolio Viewer's holdings table for DS70 (the trading app's
// approved set) and the analyst books' holdings in force today. Same markup and CSS
// classes as the viewer, so it renders identically; plus each column's basis/as-at,
// unresolved-symbol and fee-coverage notes, and the SAA tilt footer.
import React, { useMemo, useState } from 'react';
import { PERF_COLUMNS, fmtDate, fmtPct, getAssetClassColor, tiltAcColour, tiltAcRank } from './apUtils';
import { Notice, TiltBar } from './apComponents';

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const asAtMonth = (d) => {
  if (!d) return '-';
  const [y, m] = String(d).split('-');
  return (y && m) ? `${MONTHS_SHORT[+m - 1]}-${y.slice(2)}` : d;
};
const LABEL_SPAN = 3 + PERF_COLUMNS.length + 1;   // Fund Name, APIR, As At, perf columns, Report

export default function TargetWeightsTab({ data, screenerPerf, fundsWithReports, onOpenReport, tableRef }) {
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('desc');
  const cols = useMemo(() => data?.portfolios || [], [data]);
  const sortColEff = sortCol && cols.some((c) => c.code === sortCol) ? sortCol : cols[cols.length - 1]?.code;

  const grouped = useMemo(() => {
    const groups = {};
    (data?.holdings || []).forEach((h) => { (groups[h.asset_class_group] = groups[h.asset_class_group] || []).push(h); });
    if (sortColEff) {
      Object.values(groups).forEach((rows) => rows.sort((a, b) => {
        const wa = a.weights[sortColEff] || 0;
        const wb = b.weights[sortColEff] || 0;
        return sortDir === 'asc' ? wa - wb : wb - wa;
      }));
    }
    return groups;
  }, [data, sortColEff, sortDir]);

  const totals = useMemo(() => {
    const byClass = {};
    const byCol = {};
    (data?.holdings || []).forEach((h) => {
      Object.entries(h.weights).forEach(([code, w]) => {
        byCol[code] = (byCol[code] || 0) + w;
        byClass[h.asset_class_group] = byClass[h.asset_class_group] || {};
        byClass[h.asset_class_group][code] = (byClass[h.asset_class_group][code] || 0) + w;
      });
    });
    return { byClass, byCol };
  }, [data]);

  const tiltGroups = useMemo(() => {
    const present = new Set();
    let max = 2;
    cols.forEach((c) => Object.entries(data?.tilts?.[c.code] || {}).forEach(([acg, v]) => {
      if (Math.abs(v) > 0.05) present.add(acg);
      if (Math.abs(v) > max) max = Math.abs(v);
    }));
    return { items: [...present].sort((a, b) => (tiltAcRank(a) - tiltAcRank(b)) || a.localeCompare(b)), max };
  }, [data, cols]);

  if (!data?.holdings?.length) {
    return <div className="no-data-message"><p>No holdings for the selected portfolio(s).</p></div>;
  }

  const summary = data.summary || {};
  const metric = (code, key, dp = 1) => (summary[code]?.[key] == null ? '–' : `${summary[code][key].toFixed(dp)}%`);
  const feeGaps = data.fee_gaps || {};
  const unmapped = data.unmapped_sacs || {};

  return (
    <>
      {data.issues?.length > 0 && (
        <Notice kind="warn">
          Not on the AP List exactly once, so shown with the name and class saved with them (FX and fees unknown):{' '}
          {data.issues.map((i) => `${i.code} ${i.symbol} (${i.fund_name}, ${i.ap_list_matches} matches)`).join('; ')}
        </Notice>
      )}
      {Object.keys(unmapped).length > 0 && (
        <Notice kind="warn">
          Sub-asset classes with no broad class in Investment Config (left out of the SAA tilts):{' '}
          {Object.entries(unmapped).map(([c, u]) => `${c}: ${Object.keys(u).join(', ')}`).join('; ')}
        </Notice>
      )}
      <div className="table-container holdings-table-container">
        <table className="holdings-table" ref={tableRef}>
          <thead>
            <tr>
              <th className="name-col">Fund Name</th>
              <th className="code-col">APIR</th>
              <th className="asat-col" title="Month-end the fund's returns are calculated to">As At</th>
              {PERF_COLUMNS.map((c) => <th key={c.key} className="perf-col">{c.label}</th>)}
              <th className="report-col">Report</th>
              {cols.map((c) => (
                <th key={c.code} className="weight-col sortable" title="Sort holdings by this book's weights"
                  onClick={() => {
                    if (sortColEff === c.code) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                    else { setSortCol(c.code); setSortDir('desc'); }
                  }}>
                  {c.name}
                  <span className="ap-col-asat">{c.basis}{c.as_at ? ` · ${fmtDate(c.as_at)}` : ''}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(grouped).map(([assetClass, rows], gi) => (
              <React.Fragment key={assetClass}>
                <tr className="asset-class-header">
                  <td colSpan={LABEL_SPAN}>
                    <span className="asset-class-indicator" style={{ backgroundColor: getAssetClassColor(assetClass, gi) }} />
                    {assetClass}
                  </td>
                  {cols.map((c) => (
                    <td key={c.code} className="weight-col asset-class-total">
                      {c.as_at ? `${(totals.byClass[assetClass]?.[c.code] || 0).toFixed(1)}%` : '–'}
                    </td>
                  ))}
                </tr>
                {rows.map((h) => {
                  const perf = screenerPerf[h.tr_code];
                  return (
                    <tr key={`${assetClass}-${h.tr_code || h.apir_code || h.asset_name}`} className="holding-row">
                      <td className="name-col">{h.asset_name}</td>
                      <td className="code-col">{h.apir_code}</td>
                      <td className="asat-col" title={perf?.as_at_date ? `Returns to ${perf.as_at_date}` : undefined}>
                        {asAtMonth(perf?.as_at_date)}
                      </td>
                      {PERF_COLUMNS.map((pc) => {
                        const v = perf ? perf[pc.key] : null;
                        const cls = v > 0 ? 'perf-positive' : v < 0 ? 'perf-negative' : '';
                        return <td key={pc.key} className={`perf-col ${cls}`}>{perf ? fmtPct(v, 1) : '-'}</td>;
                      })}
                      <td className="report-col">
                        {h.apir_code && fundsWithReports.has(h.apir_code) && (
                          <button className="report-link-btn" title="View Research Report"
                            onClick={() => onOpenReport(h.apir_code)}>📄</button>
                        )}
                      </td>
                      {cols.map((c) => (
                        <td key={c.code} className="weight-col">
                          {h.weights[c.code] !== undefined ? `${h.weights[c.code].toFixed(1)}%` : '-'}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </React.Fragment>
            ))}
            <tr className="total-row">
              <td colSpan={LABEL_SPAN}>Total</td>
              {cols.map((c) => <td key={c.code} className="weight-col">{c.as_at ? `${(totals.byCol[c.code] || 0).toFixed(1)}%` : '–'}</td>)}
            </tr>
            <tr className="metric-row">
              <td colSpan={LABEL_SPAN}>FX Exposure</td>
              {cols.map((c) => <td key={c.code} className="weight-col">{metric(c.code, 'fx_exposure')}</td>)}
            </tr>
            <tr className="metric-row growth-row">
              <td colSpan={LABEL_SPAN}>Growth Exposure</td>
              {cols.map((c) => <td key={c.code} className="weight-col">{metric(c.code, 'growth_exposure')}</td>)}
            </tr>
            <tr className="metric-row defensive-row">
              <td colSpan={LABEL_SPAN}>Defensive Exposure</td>
              {cols.map((c) => <td key={c.code} className="weight-col">{metric(c.code, 'defensive_exposure')}</td>)}
            </tr>
            <tr className="metric-row fee-row">
              <td colSpan={LABEL_SPAN}
                title="Weighted-average cost: Σ (weight ÷ 100 × Management Fees & Costs %). Fee per fund: set in this app (Manage Portfolio), else the trading app's manual override, else the FE fee table, else Shadow Manager_Fee">
                Fees &amp; Costs (wtd)
              </td>
              {cols.map((c) => (
                <td key={c.code} className="weight-col"
                  title={feeGaps[c.code] ? `No fee data for: ${feeGaps[c.code].funds.join(', ')}` : undefined}>
                  {metric(c.code, 'fees_costs_wtd', 2)}{feeGaps[c.code] ? ' *' : ''}
                </td>
              ))}
            </tr>
            <tr className="metric-row fee-row net-row">
              <td colSpan={LABEL_SPAN} title="Weighted-average cost after rebates">Net (wtd)</td>
              {cols.map((c) => <td key={c.code} className="weight-col">{metric(c.code, 'net_wtd', 2)}{feeGaps[c.code] ? ' *' : ''}</td>)}
            </tr>
            {tiltGroups.items.length > 0 && (
              <>
                <tr className="pv-tilt-head">
                  <td colSpan={LABEL_SPAN} title="Deviation from DS70's SAA neutral — bar right = overweight, left = underweight">
                    SAA tilts (vs the DS70 SAA)
                  </td>
                  {cols.map((c) => <td key={c.code} className="weight-col" />)}
                </tr>
                {tiltGroups.items.map((acg) => {
                  const colour = tiltAcColour(acg, tiltAcRank(acg));
                  return (
                    <tr key={`tilt-${acg}`} className="pv-tilt-ac">
                      <td colSpan={LABEL_SPAN}><span className="ac-dot" style={{ background: colour }} />{acg}</td>
                      {cols.map((c) => {
                        const v = data.tilts?.[c.code]?.[acg];
                        return (
                          <td key={c.code} className="weight-col pv-tilt-cell">
                            {v == null ? null : <TiltBar value={v} max={tiltGroups.max} color={colour} />}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </>
            )}
          </tbody>
        </table>
      </div>
      {Object.keys(feeGaps).length > 0 && (
        <p className="ap-footnote">
          * Fees &amp; Costs leave out holdings with no fee in any source:{' '}
          {Object.entries(feeGaps).map(([c, g]) => `${c} ${fmtPct(g.weight, 1)} (${g.funds.join(', ')})`).join('; ')}.
        </p>
      )}
      <p className="ap-footnote">
        DS70 = the trading app&apos;s approved target set; analyst books = the holdings saved in this app, in force today
        ({fmtDate(data.as_at_date)}). Names, APIRs and asset classes are read live from the AP List.
      </p>
    </>
  );
}
